const nebula = {
  // Configuration
  config: {
    mobileBreakpoint: 750,
  },

  // Footer brand info management
  footer: {
    brandInfoElement: null,
    mobileBrandElement: null,
    originalParent: null,

    /**
     * Initialize footer brand info responsive behavior
     */
    init() {
      // Find the inner brand info div (the one with actual content)
      // First find the footer-block wrapper, then the inner div
      const brandBlockWrapper = document.querySelector('.footer-block.footer-block__brand-info');
      this.brandInfoElement = brandBlockWrapper ? brandBlockWrapper.querySelector('.footer-block__brand-info') : null;
      this.mobileBrandElement = document.querySelector('.footer__mobile-brand');

      if (!this.brandInfoElement || !this.mobileBrandElement) {
        return;
      }

      // Store original parent
      this.originalParent = this.brandInfoElement.parentElement;

      // Handle initial load
      this.handleResize();

      // Listen for resize events with proper context binding
      const debouncedResize = this.debounce(() => {
        this.handleResize();
      }, 250);
      window.addEventListener('resize', debouncedResize);
    },

    /**
     * Handle resize event - move element based on screen size
     */
    handleResize() {
      const isMobile = window.innerWidth < nebula.config.mobileBreakpoint;

      if (isMobile) {
        this.moveToMobile();
      } else {
        this.moveToOriginal();
      }
    },

    /**
     * Move brand info element to mobile brand container
     */
    moveToMobile() {
      if (
        this.brandInfoElement &&
        this.mobileBrandElement &&
        this.brandInfoElement.parentElement !== this.mobileBrandElement
      ) {
        this.mobileBrandElement.appendChild(this.brandInfoElement);
      }
    },

    /**
     * Move brand info element back to original location
     */
    moveToOriginal() {
      if (this.brandInfoElement && this.originalParent && this.brandInfoElement.parentElement !== this.originalParent) {
        this.originalParent.appendChild(this.brandInfoElement);
      }
    },

    /**
     * Debounce utility function
     */
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },
  },
  // Rebuy widget customization
  rebuy: {
    widgetId: '251764',

    init() {
      if (!document.body.classList.contains('template-product')) {
        return;
      }

      console.log('[Nebula Rebuy] Initializing widget logic for', this.widgetId);

      const eventsToWatch = [
        'rebuy.init',
        'rebuy.ready',
        'rebuy.refresh',
        'rebuy.productsChange',
        'rebuy.show',
        'rebuy.add',
      ];

      eventsToWatch.forEach((eventName) => {
        document.addEventListener(eventName, (event) => {
          if (!this.isTargetWidgetEvent(event)) {
            return;
          }

          // Run on the next frame so Rebuy can finish rendering
          window.requestAnimationFrame(() => {
            this.applyLayout();
          });
        });
      });

      // Fallback in case the widget is already rendered before listeners run
      window.requestAnimationFrame(() => {
        const exists = Boolean(document.getElementById(`rebuy-widget-${this.widgetId}`));
        if (exists) {
          this.applyLayout();
        }
      });
    },

    isTargetWidgetEvent(event) {
      const detail = event && event.detail ? event.detail : {};
      const detailId = this.extractWidgetId(detail);

      if (detailId) {
        return String(detailId) === this.widgetId;
      }

      // Fallback: run if the widget exists in the DOM
      const fallbackFound = Boolean(document.getElementById(`rebuy-widget-${this.widgetId}`));
      return fallbackFound;
    },

    extractWidgetId(detail) {
      if (!detail) {
        return null;
      }

      if (detail.widget && detail.widget.id) {
        return detail.widget.id;
      }

      if (detail.id) {
        return detail.id;
      }

      if (detail.widgetId) {
        return detail.widgetId;
      }

      return null;
    },

    applyLayout() {
      const widgetElement = document.getElementById(`rebuy-widget-${this.widgetId}`);

      if (!widgetElement) {
        console.log('[Nebula Rebuy] Widget element not found in DOM, aborting layout');
        return;
      }

      const productBlocks = widgetElement.querySelectorAll('.rebuy-product-block');

      productBlocks.forEach((block) => {
        // Always ensure the nebula-rebuy-card class is present
        block.classList.add('nebula-rebuy-card');

        // If layout was already applied, just ensure classes/styles are maintained
        if (block.dataset.nebulaRebuyLayout === 'applied') {
          // Ensure button classes are still present
          const button = block.querySelector('button.rebuy-button');
          if (button) {
            button.classList.add('button', 'button--primary');
          }
          return;
        }

        const info = block.querySelector('.rebuy-product-info');
        const actions = block.querySelector('.rebuy-product-actions');

        if (!info || !actions) {
          return;
        }

        const price = info.querySelector('.rebuy-product-price');

        if (!price) {
          return;
        }

        // Check if bottom row already exists
        const existingBottomRow = info.querySelector('.nebula-rebuy-bottom-row');
        if (existingBottomRow) {
          // Ensure button classes are still present
          const button = actions.querySelector('button.rebuy-button');
          if (button) {
            button.classList.add('button', 'button--primary');
          }
          block.dataset.nebulaRebuyLayout = 'applied';
          return;
        }

        const button = actions.querySelector('button.rebuy-button');
        if (button) {
          button.classList.add('button', 'button--primary');
        }

        const bottomRow = document.createElement('div');
        bottomRow.className = 'nebula-rebuy-bottom-row';

        bottomRow.appendChild(price);
        bottomRow.appendChild(actions);

        info.appendChild(bottomRow);

        block.dataset.nebulaRebuyLayout = 'applied';
      });
    },
  },
  // Rebuy widget title height normalization (supports multiple widgets)
  rebuyTitleHeight: {
    widgetIds: ['252472', '253923', '254233', '255003'],
    resizeDelay: 200,
    eventDelay: 150,
    resizeHandler: null,
    pendingUpdate: null,

    init() {
      const eventsToWatch = ['rebuy.init', 'rebuy.ready', 'rebuy.refresh', 'rebuy.productsChange', 'rebuy.show'];
      eventsToWatch.forEach((eventName) => {
        document.addEventListener(eventName, (event) => {
          if (!this.isTargetWidgetEvent(event)) {
            return;
          }
          this.scheduleUpdate();
        });
      });

      // Initial run in case the widget is already rendered
      this.scheduleUpdate();

      // Handle resizes with debounce and slight delay
      if (!this.resizeHandler) {
        this.resizeHandler = this.debounce(() => {
          this.scheduleUpdate();
        }, this.resizeDelay);
      }
      window.addEventListener('resize', this.resizeHandler);
    },

    debounce(func, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          func(...args);
        }, wait);
      };
    },

    isTargetWidgetEvent(event) {
      const detail = event && event.detail ? event.detail : {};
      const detailId = this.extractWidgetId(detail);

      if (detailId) {
        return this.widgetIds.includes(String(detailId));
      }

      return this.widgetIds.some((id) => Boolean(document.getElementById(`rebuy-widget-${id}`)));
    },

    extractWidgetId(detail) {
      if (!detail) {
        return null;
      }

      if (detail.widget && detail.widget.id) {
        return detail.widget.id;
      }

      if (detail.id) {
        return detail.id;
      }

      if (detail.widgetId) {
        return detail.widgetId;
      }

      return null;
    },

    scheduleUpdate() {
      if (this.pendingUpdate) {
        clearTimeout(this.pendingUpdate);
      }

      this.pendingUpdate = setTimeout(() => {
        window.requestAnimationFrame(() => {
          this.applyEqualHeights();
        });
      }, this.eventDelay);
    },

    applyEqualHeights() {
      this.widgetIds.forEach((widgetId) => {
        const widgetElement = document.getElementById(`rebuy-widget-${widgetId}`);
        if (!widgetElement) {
          return;
        }

        // Normalize product title heights
        const titleLinks = widgetElement.querySelectorAll('.rebuy-product-title a');
        if (titleLinks.length > 0) {
          // Reset any existing min-heights before measurement
          titleLinks.forEach((link) => {
            link.style.minHeight = '';
          });

          let maxTitleHeight = 0;
          titleLinks.forEach((link) => {
            const height = link.getBoundingClientRect().height;
            if (height > maxTitleHeight) {
              maxTitleHeight = height;
            }
          });

          if (maxTitleHeight > 0) {
            const normalizedTitleHeight = `${Math.ceil(maxTitleHeight)}px`;
            titleLinks.forEach((link) => {
              link.style.minHeight = normalizedTitleHeight;
            });
          }
        }

        // Normalize subtitle heights (only for subtitles with content)
        const subtitles = widgetElement.querySelectorAll('.rebuy-subtitle');
        const subtitlesWithContent = Array.from(subtitles).filter(
          (subtitle) => subtitle.textContent && subtitle.textContent.trim() !== '',
        );

        if (subtitlesWithContent.length > 0) {
          // Reset any existing min-heights before measurement
          subtitlesWithContent.forEach((subtitle) => {
            subtitle.style.minHeight = '';
          });

          let maxSubtitleHeight = 0;
          subtitlesWithContent.forEach((subtitle) => {
            const height = subtitle.getBoundingClientRect().height;
            if (height > maxSubtitleHeight) {
              maxSubtitleHeight = height;
            }
          });

          if (maxSubtitleHeight > 0) {
            const normalizedSubtitleHeight = `${Math.ceil(maxSubtitleHeight)}px`;
            subtitlesWithContent.forEach((subtitle) => {
              subtitle.style.minHeight = normalizedSubtitleHeight;
            });
          }
        }

        // Normalize product review heights (only for reviews with content)
        const reviews = widgetElement.querySelectorAll('.rebuy-product-review');
        const reviewsWithContent = Array.from(reviews).filter(
          (review) => review.textContent && review.textContent.trim() !== '',
        );

        if (reviewsWithContent.length > 0) {
          // Reset any existing min-heights before measurement
          reviewsWithContent.forEach((review) => {
            review.style.minHeight = '';
          });

          let maxReviewHeight = 0;
          reviewsWithContent.forEach((review) => {
            const height = review.getBoundingClientRect().height;
            if (height > maxReviewHeight) {
              maxReviewHeight = height;
            }
          });

          if (maxReviewHeight > 0) {
            const normalizedReviewHeight = `${Math.ceil(maxReviewHeight)}px`;
            reviewsWithContent.forEach((review) => {
              review.style.minHeight = normalizedReviewHeight;
            });
          }
        }
      });
    },
  },

  // Format Rebuy widget review count numbers
  rebuyReviewCount: {
    widgetIds: ['252472', '253923', '254233', '255003'],
    eventDelay: 200,
    processedElements: new WeakSet(),

    init() {
      const eventsToWatch = ['rebuy.init', 'rebuy.ready', 'rebuy.refresh', 'rebuy.productsChange', 'rebuy.show'];

      eventsToWatch.forEach((eventName) => {
        document.addEventListener(eventName, (event) => {
          if (!this.isTargetWidgetEvent(event)) {
            return;
          }
          this.scheduleUpdate();
        });
      });

      // Initial run in case the widget is already rendered
      this.scheduleUpdate();

      // Handle resizes with debounce
      const resizeHandler = this.debounce(() => {
        this.scheduleUpdate();
      }, 250);
      window.addEventListener('resize', resizeHandler);
    },

    debounce(func, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          func(...args);
        }, wait);
      };
    },

    isTargetWidgetEvent(event) {
      const detail = event && event.detail ? event.detail : {};
      const detailId = this.extractWidgetId(detail);

      if (detailId) {
        return this.widgetIds.includes(String(detailId));
      }

      return this.widgetIds.some((id) => Boolean(document.getElementById(`rebuy-widget-${id}`)));
    },

    extractWidgetId(detail) {
      if (!detail) {
        return null;
      }

      if (detail.widget && detail.widget.id) {
        return detail.widget.id;
      }

      if (detail.id) {
        return detail.id;
      }

      if (detail.widgetId) {
        return detail.widgetId;
      }

      return null;
    },

    scheduleUpdate() {
      setTimeout(() => {
        window.requestAnimationFrame(() => {
          this.formatReviewCounts();
        });
      }, this.eventDelay);
    },

    formatReviewCounts() {
      this.widgetIds.forEach((widgetId) => {
        const widgetElement = document.getElementById(`rebuy-widget-${widgetId}`);
        if (!widgetElement) {
          return;
        }

        const reviewCountElements = widgetElement.querySelectorAll('.rebuy-review-count-number');

        reviewCountElements.forEach((element) => {
          // Skip if already processed
          if (this.processedElements.has(element)) {
            return;
          }

          const originalText = element.textContent.trim();
          if (!originalText) {
            return;
          }

          // Remove commas and parse the number
          const number = parseInt(originalText.replace(/,/g, ''), 10);

          if (isNaN(number)) {
            return;
          }

          // Format if >= 1000
          if (number >= 1000) {
            const thousands = Math.floor(number / 1000);
            const hundreds = Math.floor((number % 1000) / 100);
            element.textContent = `${thousands}.${hundreds}K`;
          }

          // Mark as processed
          this.processedElements.add(element);
        });
      });
    },
  },

  // Format Okendo review count numbers
  okendoReviewCount: {
    eventDelay: 200,
    processedElements: new WeakSet(),
    observer: null,

    init() {
      // Format existing review counts
      this.formatReviewCounts();

      // Watch for dynamically added review counts (Okendo loads asynchronously)
      if (!this.observer) {
        this.observer = new MutationObserver(() => {
          this.formatReviewCounts();
        });

        this.observer.observe(document.body, {
          childList: true,
          subtree: true,
        });
      }

      // Also check periodically for Okendo elements that load late
      const checkInterval = setInterval(() => {
        const okendoElements = document.querySelectorAll('.oke-sr-count-number');
        if (okendoElements.length > 0) {
          this.formatReviewCounts();
        }
      }, 1000);

      // Stop checking after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
      }, 10000);
    },

    formatReviewCounts() {
      const reviewCountElements = document.querySelectorAll('.oke-sr-count-number');

      reviewCountElements.forEach((element) => {
        // Skip if already processed
        if (this.processedElements.has(element)) {
          return;
        }

        const originalText = element.textContent.trim();
        if (!originalText) {
          return;
        }

        // Remove commas and parse the number
        const number = parseInt(originalText.replace(/,/g, ''), 10);

        if (isNaN(number)) {
          return;
        }

        // Format if >= 1000
        if (number >= 1000) {
          const thousands = Math.floor(number / 1000);
          const hundreds = Math.floor((number % 1000) / 100);
          element.textContent = `${thousands}.${hundreds}K`;
        }

        // Mark as processed
        this.processedElements.add(element);
      });
    },
  },

  // Remove URL parameters from Rebuy widget links (widget 252472)
  rebuyCleanLinks: {
    widgetId: '252472',
    eventDelay: 200,

    init() {
      const eventsToWatch = ['rebuy.init', 'rebuy.ready', 'rebuy.refresh', 'rebuy.productsChange', 'rebuy.show'];

      eventsToWatch.forEach((eventName) => {
        document.addEventListener(eventName, (event) => {
          if (!this.isTargetWidgetEvent(event)) {
            return;
          }
          this.scheduleUpdate();
        });
      });

      // Initial run in case the widget is already rendered
      this.scheduleUpdate();
    },

    isTargetWidgetEvent(event) {
      const detail = event && event.detail ? event.detail : {};
      const detailId = this.extractWidgetId(detail);

      if (detailId) {
        return String(detailId) === this.widgetId;
      }

      return Boolean(document.getElementById(`rebuy-widget-${this.widgetId}`));
    },

    extractWidgetId(detail) {
      if (!detail) {
        return null;
      }

      if (detail.widget && detail.widget.id) {
        return detail.widget.id;
      }

      if (detail.id) {
        return detail.id;
      }

      if (detail.widgetId) {
        return detail.widgetId;
      }

      return null;
    },

    scheduleUpdate() {
      setTimeout(() => {
        window.requestAnimationFrame(() => {
          this.cleanLinks();
        });
      }, this.eventDelay);
    },

    cleanLinks() {
      const widgetElement = document.getElementById(`rebuy-widget-${this.widgetId}`);
      if (!widgetElement) {
        return;
      }

      // Clean product image links
      const productImageLinks = widgetElement.querySelectorAll('.rebuy-product-image');
      productImageLinks.forEach((link) => {
        const href = link.getAttribute('href');
        if (href) {
          const cleanHref = href.split('?')[0];
          if (href !== cleanHref) {
            link.setAttribute('href', cleanHref);
          }
        }
      });

      // Clean product title links
      const productTitleLinks = widgetElement.querySelectorAll('.rebuy-product-title-link');
      productTitleLinks.forEach((link) => {
        const href = link.getAttribute('href');
        if (href) {
          const cleanHref = href.split('?')[0];
          if (href !== cleanHref) {
            link.setAttribute('href', cleanHref);
          }
        }
      });
    },
  },

  // Rebuy widget product subtitle metafield population
  rebuySubtitle: {
    widgetIds: ['252472', '253923', '254233', '255003'],
    metafieldData: {},
    processedProducts: new Set(),
    shopDomain: null,
    accessToken: null,
    apiEndpoint: null,

    init() {
      // Get Storefront API config
      if (!window.rebuySubtitleConfig) {
        return;
      }

      this.shopDomain = window.rebuySubtitleConfig.shopDomain;
      this.accessToken = window.rebuySubtitleConfig.accessToken;
      this.apiEndpoint = `https://${this.shopDomain}/api/2024-01/graphql.json`;

      const eventsToWatch = ['rebuy.init', 'rebuy.ready', 'rebuy.refresh', 'rebuy.productsChange', 'rebuy.show'];

      eventsToWatch.forEach((eventName) => {
        document.addEventListener(eventName, (event) => {
          if (!this.isTargetWidgetEvent(event)) {
            return;
          }
          this.scheduleUpdate();
        });
      });

      // Initial run only if any target widget exists
      const hasWidget = this.widgetIds.some((id) => Boolean(document.getElementById(`rebuy-widget-${id}`)));
      if (hasWidget) {
        this.scheduleUpdate();
      }
    },

    isTargetWidgetEvent(event) {
      const detail = event && event.detail ? event.detail : {};
      const detailId = this.extractWidgetId(detail);

      if (detailId) {
        return this.widgetIds.includes(String(detailId));
      }

      return this.widgetIds.some((id) => Boolean(document.getElementById(`rebuy-widget-${id}`)));
    },

    extractWidgetId(detail) {
      if (!detail) {
        return null;
      }

      if (detail.widget && detail.widget.id) {
        return detail.widget.id;
      }

      if (detail.id) {
        return detail.id;
      }

      if (detail.widgetId) {
        return detail.widgetId;
      }

      return null;
    },

    scheduleUpdate() {
      setTimeout(() => {
        window.requestAnimationFrame(async () => {
          await this.populateSubtitles();
        });
      }, 200);
    },

    getProductId(block) {
      // Extract product ID from class name (e.g., "product-id-6541740605498")
      const classList = Array.from(block.classList);
      const productIdClass = classList.find((cls) => cls.startsWith('product-id-'));
      if (productIdClass) {
        return productIdClass.replace('product-id-', '');
      }
      return null;
    },

    async populateSubtitles() {
      if (!this.apiEndpoint || !this.accessToken) {
        return;
      }

      // Collect product IDs that need metafields from all target widgets
      const productIdsToFetch = [];
      const productIdToElementMap = new Map();

      this.widgetIds.forEach((widgetId) => {
        const widgetElement = document.getElementById(`rebuy-widget-${widgetId}`);
        if (!widgetElement) {
          return;
        }

        const productBlocks = widgetElement.querySelectorAll('.rebuy-product-block');
        if (!productBlocks.length) {
          return;
        }

        productBlocks.forEach((block) => {
          const subtitleElement = block.querySelector('.rebuy-subtitle');
          if (!subtitleElement) {
            return;
          }

          const productId = this.getProductId(block);
          if (!productId) {
            return;
          }

          // Skip if already processed or already in cache
          if (this.processedProducts.has(productId) || this.metafieldData[productId]) {
            const cachedValue = this.metafieldData[productId];
            if (cachedValue && cachedValue.trim() !== '') {
              const formattedValue = this.formatSubtitle(cachedValue);
              subtitleElement.textContent = formattedValue;
              this.processedProducts.add(productId);
            }
            return;
          }

          // Add to fetch list (avoid duplicates across widgets)
          if (!productIdsToFetch.includes(productId)) {
            productIdsToFetch.push(productId);
            productIdToElementMap.set(productId, subtitleElement);
          }
        });
      });

      // Fetch metafields for products that need them
      if (productIdsToFetch.length > 0) {
        await this.fetchMetafieldsFromAPI(productIdsToFetch, productIdToElementMap);
      }

      // Trigger height normalization after subtitles are populated (for widgets that need it)
      const widgetsNeedingHeightNormalization = ['252472', '253923', '254233', '255003'];
      if (this.widgetIds.some((id) => widgetsNeedingHeightNormalization.includes(id))) {
        setTimeout(() => {
          nebula.rebuyTitleHeight.applyEqualHeights();
        }, 100);
      }
    },

    async fetchMetafieldsFromAPI(productIds, elementMap) {
      // Convert Shopify product IDs to GID format for GraphQL
      const productGids = productIds.map((id) => `gid://shopify/Product/${id}`);

      const query = `
        query getProductMetafields($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on Product {
              id
              metafield(namespace: "my_fields", key: "product_card_subtitle") {
                value
              }
            }
          }
        }
      `;

      try {
        const response = await fetch(this.apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.accessToken,
          },
          body: JSON.stringify({
            query,
            variables: {
              ids: productGids,
            },
          }),
        });

        if (!response.ok) {
          console.error(`[Nebula Rebuy Subtitle] API request failed: ${response.status} ${response.statusText}`);
          return;
        }

        const result = await response.json();

        if (result.errors) {
          console.error('[Nebula Rebuy Subtitle] GraphQL errors:', result.errors);
          return;
        }

        // Process results
        if (result.data && result.data.nodes) {
          result.data.nodes.forEach((node) => {
            if (!node || !node.id) {
              return;
            }

            // Extract numeric ID from GID (gid://shopify/Product/6541740605498)
            const idMatch = node.id.match(/\/(\d+)$/);
            if (!idMatch) {
              return;
            }

            const productId = idMatch[1];
            const metafieldValue = node.metafield?.value;

            // Cache the metafield value
            if (metafieldValue) {
              this.metafieldData[productId] = metafieldValue;
            }

            // Update subtitle elements for this product across all target widgets
            if (metafieldValue && metafieldValue.trim() !== '') {
              const formattedValue = this.formatSubtitle(metafieldValue);

              // Update the element from the map (if it exists)
              const subtitleElement = elementMap.get(productId);
              if (subtitleElement) {
                subtitleElement.textContent = formattedValue;
              }

              // Also update any other subtitle elements for this product in all widgets
              this.widgetIds.forEach((widgetId) => {
                const widgetElement = document.getElementById(`rebuy-widget-${widgetId}`);
                if (!widgetElement) {
                  return;
                }

                const productBlocks = widgetElement.querySelectorAll(`.product-id-${productId}`);
                productBlocks.forEach((block) => {
                  const subtitleEl = block.querySelector('.rebuy-subtitle');
                  if (subtitleEl) {
                    subtitleEl.textContent = formattedValue;
                  }
                });
              });

              this.processedProducts.add(productId);
            }
          });
        }
      } catch (error) {
        console.error('[Nebula Rebuy Subtitle] Error fetching metafields:', error);
      }
    },

    formatSubtitle(value) {
      // Split by | and take only the first two values
      if (!value || typeof value !== 'string') {
        return value;
      }

      const parts = value
        .split('|')
        .map((part) => part.trim())
        .filter((part) => part !== '');
      const limitedParts = parts.slice(0, 2);
      return limitedParts.join(' | ');
    },

    getProductId(block) {
      // Extract product ID from class name (e.g., "product-id-6541740605498")
      const classList = Array.from(block.classList);
      const productIdClass = classList.find((cls) => cls.startsWith('product-id-'));
      if (productIdClass) {
        return productIdClass.replace('product-id-', '');
      }
      return null;
    },
  },

  // Smooth scrolling for article navigation links
  smoothScroll: {
    /**
     * Initialize smooth scrolling for article template navigation links
     */
    init() {
      const navLinks = document.querySelectorAll('.article-template__nav-link');

      navLinks.forEach((link) => {
        link.addEventListener('click', (e) => {
          const href = link.getAttribute('href');

          // Only handle anchor links (starting with #)
          if (href && href.startsWith('#')) {
            const targetId = href.substring(1); // Remove the # symbol
            const targetElement = document.getElementById(targetId);

            if (targetElement) {
              e.preventDefault();

              // Smooth scroll to the target element
              targetElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }
          }
        });
      });
    },
  },

  // Collection page card heading height normalization
  collectionCardHeights: {
    resizeDelay: 250,
    mutationDelay: 150,
    resizeHandler: null,
    pendingUpdate: null,
    observer: null,

    /**
     * Initialize card heading height normalization for collection pages
     */
    init() {
      // Only run on collection pages
      if (!document.body.classList.contains('page-collection')) {
        return;
      }

      // Initial run
      this.scheduleUpdate();

      // Handle resizes with debounce
      if (!this.resizeHandler) {
        this.resizeHandler = this.debounce(() => {
          this.scheduleUpdate();
        }, this.resizeDelay);
      }
      window.addEventListener('resize', this.resizeHandler);

      // Watch for product grid changes (filtering)
      this.setupMutationObserver();
    },

    /**
     * Debounce utility function
     */
    debounce(func, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          func(...args);
        }, wait);
      };
    },

    /**
     * Setup MutationObserver to watch for product grid updates
     */
    setupMutationObserver() {
      const productGridContainer = document.getElementById('ProductGridContainer');
      if (!productGridContainer || !window.MutationObserver) {
        return;
      }

      this.observer = new MutationObserver(() => {
        this.scheduleUpdate();
      });

      this.observer.observe(productGridContainer, {
        childList: true,
        subtree: true,
      });
    },

    /**
     * Schedule an update with debouncing
     */
    scheduleUpdate() {
      if (this.pendingUpdate) {
        clearTimeout(this.pendingUpdate);
      }

      this.pendingUpdate = setTimeout(() => {
        window.requestAnimationFrame(() => {
          this.applyEqualHeights();
        });
      }, this.mutationDelay);
    },

    /**
     * Group cards into rows based on their vertical position
     */
    groupCardsByRow(cards) {
      const rows = [];
      const rowTolerance = 20; // Pixels tolerance for grouping cards in the same row

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const top = rect.top;

        // Find existing row with similar top position
        let foundRow = null;
        for (const row of rows) {
          const rowTop = row[0].getBoundingClientRect().top;
          if (Math.abs(top - rowTop) <= rowTolerance) {
            foundRow = row;
            break;
          }
        }

        if (foundRow) {
          foundRow.push(card);
        } else {
          rows.push([card]);
        }
      });

      return rows;
    },

    /**
     * Apply equal heights to card headings and subtitles within each row
     */
    applyEqualHeights() {
      const productGrid = document.getElementById('product-grid');
      if (!productGrid) {
        return;
      }

      // Get all card elements (using card-wrapper as the container)
      const cardWrappers = productGrid.querySelectorAll('.card-wrapper');
      if (!cardWrappers.length) {
        return;
      }

      // Group cards by row
      const cardRows = this.groupCardsByRow(Array.from(cardWrappers));

      // Process each row separately
      cardRows.forEach((rowCards) => {
        // Reset min-heights for headings in this row
        const rowHeadings = [];
        rowCards.forEach((cardWrapper) => {
          const heading = cardWrapper.querySelector('.card__heading');
          if (heading) {
            heading.style.minHeight = '';
            rowHeadings.push(heading);
          }
        });

        // Normalize headings within this row
        if (rowHeadings.length > 0) {
          let maxHeight = 0;
          rowHeadings.forEach((heading) => {
            const height = heading.getBoundingClientRect().height;
            if (height > maxHeight) {
              maxHeight = height;
            }
          });

          if (maxHeight > 0) {
            const normalizedHeight = `${Math.ceil(maxHeight)}px`;
            rowHeadings.forEach((heading) => {
              heading.style.minHeight = normalizedHeight;
            });
          }
        }

        // Reset min-heights for subtitles in this row
        const rowSubtitles = [];
        rowCards.forEach((cardWrapper) => {
          const subtitle = cardWrapper.querySelector('.card__subtitle');
          if (subtitle) {
            subtitle.style.minHeight = '';
            rowSubtitles.push(subtitle);
          }
        });

        // Normalize subtitles within this row
        if (rowSubtitles.length > 0) {
          let maxSubtitleHeight = 0;
          rowSubtitles.forEach((subtitle) => {
            const height = subtitle.getBoundingClientRect().height;
            if (height > maxSubtitleHeight) {
              maxSubtitleHeight = height;
            }
          });

          if (maxSubtitleHeight > 0) {
            const normalizedSubtitleHeight = `${Math.ceil(maxSubtitleHeight)}px`;
            rowSubtitles.forEach((subtitle) => {
              subtitle.style.minHeight = normalizedSubtitleHeight;
            });
          }
        }
      });
    },
  },

  // Rebuy widget card height normalization
  rebuyCardHeights: {
    widgetId: '253923',
    resizeDelay: 250,
    eventDelay: 200,
    resizeHandler: null,
    pendingUpdate: null,

    init() {
      const eventsToWatch = ['rebuy.init', 'rebuy.ready', 'rebuy.refresh', 'rebuy.productsChange', 'rebuy.show'];

      eventsToWatch.forEach((eventName) => {
        document.addEventListener(eventName, (event) => {
          if (!this.isTargetWidgetEvent(event)) {
            return;
          }
          this.scheduleUpdate();
        });
      });

      // Initial run in case the widget is already rendered
      this.scheduleUpdate();

      // Handle resizes with debounce
      if (!this.resizeHandler) {
        this.resizeHandler = this.debounce(() => {
          this.scheduleUpdate();
        }, this.resizeDelay);
      }
      window.addEventListener('resize', this.resizeHandler);
    },

    debounce(func, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          func(...args);
        }, wait);
      };
    },

    isTargetWidgetEvent(event) {
      const detail = event && event.detail ? event.detail : {};
      const detailId = this.extractWidgetId(detail);

      if (detailId) {
        return String(detailId) === this.widgetId;
      }

      return Boolean(document.getElementById(`rebuy-widget-${this.widgetId}`));
    },

    extractWidgetId(detail) {
      if (!detail) {
        return null;
      }

      if (detail.widget && detail.widget.id) {
        return detail.widget.id;
      }

      if (detail.id) {
        return detail.id;
      }

      if (detail.widgetId) {
        return detail.widgetId;
      }

      return null;
    },

    scheduleUpdate() {
      if (this.pendingUpdate) {
        clearTimeout(this.pendingUpdate);
      }

      this.pendingUpdate = setTimeout(() => {
        window.requestAnimationFrame(() => {
          this.applyEqualHeights();
        });
      }, this.eventDelay);
    },

    applyEqualHeights() {
      const widgetElement = document.getElementById(`rebuy-widget-${this.widgetId}`);
      if (!widgetElement) {
        return;
      }

      // Match heights for product titles
      const productTitles = widgetElement.querySelectorAll('.rebuy-product-title');
      if (productTitles.length > 0) {
        // Reset any existing min-heights before measurement
        productTitles.forEach((title) => {
          title.style.minHeight = '';
        });

        // Measure all titles
        let maxTitleHeight = 0;
        productTitles.forEach((title) => {
          const height = title.getBoundingClientRect().height;
          if (height > maxTitleHeight) {
            maxTitleHeight = height;
          }
        });

        // Apply normalized height
        if (maxTitleHeight > 0) {
          const normalizedTitleHeight = `${Math.ceil(maxTitleHeight)}px`;
          productTitles.forEach((title) => {
            title.style.minHeight = normalizedTitleHeight;
          });
        }
      }

      // Match heights for subtitles
      const subtitles = widgetElement.querySelectorAll('.rebuy-subtitle');
      if (subtitles.length > 0) {
        // Reset any existing min-heights before measurement
        subtitles.forEach((subtitle) => {
          subtitle.style.minHeight = '';
        });

        // Measure all subtitles
        let maxSubtitleHeight = 0;
        subtitles.forEach((subtitle) => {
          const height = subtitle.getBoundingClientRect().height;
          if (height > maxSubtitleHeight) {
            maxSubtitleHeight = height;
          }
        });

        // Apply normalized height
        if (maxSubtitleHeight > 0) {
          const normalizedSubtitleHeight = `${Math.ceil(maxSubtitleHeight)}px`;
          subtitles.forEach((subtitle) => {
            subtitle.style.minHeight = normalizedSubtitleHeight;
          });
        }
      }
    },
  },

  // Hide Skio title on product pages without variant picker
  productSkioTitle: {
    /**
     * Check if product page has no variant boxes and hide Skio title accordingly
     */
    init() {
      // Only run on product pages
      if (!document.body.classList.contains('page-product')) {
        return;
      }

      // Check if variant boxes exist
      const variantBoxes = document.querySelector('.variant-boxes');

      if (!variantBoxes) {
        // No variant picker, add class to body to hide Skio title
        document.body.classList.add('product-no-variants');
        console.log('[Nebula] Product has no variants, hiding Skio title');
      }
    },
  },

  // Apply typography overrides to Recharge widget purchase option selector
  rechargeWidgetTypography: {
    styleElementId: 'nebula-recharge-widget-typography',
    sellingPlansStyleElementId: 'nebula-recharge-selling-plans-style',
    benefitsStyleElementId: 'nebula-recharge-benefits-style',
    observer: null,
    shadowObservers: new WeakMap(),
    sellingPlansShadowObservers: new WeakMap(),
    benefitsShadowObservers: new WeakMap(),
    retryTimeout: null,
    retryDelay: 300,
    maxAttempts: 20,

    init() {
      // Only run on product pages
      if (!document.body.classList.contains('page-product')) {
        return;
      }

      this.startObserver();

      if (this.applyStylesToWidgets()) {
        return;
      }

      this.startRetryLoop();
    },

    applyStylesToWidgets() {
      const widgets = document.querySelectorAll('recharge-subscription-widget');
      if (widgets.length === 0) {
        return false;
      }

      let styledAtLeastOne = false;

      widgets.forEach((widget, index) => {
        if (!widget.shadowRoot) {
          return;
        }

        this.injectStyles(widget.shadowRoot, index);
        this.injectSellingPlansStyles(widget.shadowRoot);
        this.injectBenefitsStyles(widget.shadowRoot);
        this.observeShadowRoot(widget.shadowRoot, index);
        styledAtLeastOne = true;
      });

      if (!styledAtLeastOne) {
        return false;
      }

      return true;
    },

    injectStyles(shadowRoot, index = 0) {
      if (shadowRoot.getElementById(this.styleElementId)) {
        return;
      }

      const styleElement = document.createElement('style');
      styleElement.id = this.styleElementId;
      styleElement.textContent = `
        .rc-purchase-option__selector {
          line-height: 1.5;
          font-family: "GT Ultra Median", serif;
          font-size: 17px;
          font-weight: 700;
        }

        .rc-price {
          font-family: 'Brooklyn', sans-serif;
          font-size: 18px !important;
          font-weight: 900 !important;
          letter-spacing: -0.36px;
        }

        li::before {
          top: 2px !important;
        }

        .rc-purchase-option__sub-container {
          flex-direction: column-reverse !important;
        }

        .rc-purchase-option__prices {
          flex-direction: row-reverse !important;
          align-items: flex-end !important;
          gap: 6px !important;
        }

        .rc-price.strike-through {
          font-size: 14px !important;
          font-weight: 900;
          opacity: 0.6;
          letter-spacing: -0.36px;
          margin-bottom: 2px;
        }

        .rc-purchase-option__label {
          padding: 12px 14px 8px 13px !important;
        }

        .rc-purchase-option[rc-selected] {
          background: #FAF4E9 !important;
          border-color: #c2c2c2 !important;
        }

        .rc-purchase-option:not([rc-selected]), .rc-plans-dropdown__select {
          background: #FFF !important;
        }

        rc-learn-more {
          display: none !important;
        }

        .rc-purchase-option__prices {
          margin-left: 6px !important;
        }

        @media screen and (min-width: 1140px) {
          .rc-purchase-option__selector {
            font-size: 20px !important;
          }

          .rc-price {
            font-size: 20px !important;
          }

          .rc-price.strike-through {
            font-size: 16px !important;
          }

          rc-benefits, rc-selling-plans {
            margin-left: 25px !important;
          }

          .rc-purchase-option__sub-container {
            gap: 0 !important;
            padding: 0 20px 16px !important;
          }

          .rc-benefits__list li {
            margin-bottom: 10px !important;
          }

          .rc-purchase-option__label {
            padding: 15px 14px 15px 13px !important;
          }
        }
      `;

      shadowRoot.appendChild(styleElement);
    },

    injectSellingPlansStyles(widgetShadowRoot) {
      const sellingPlansElements = widgetShadowRoot.querySelectorAll('rc-selling-plans');

      sellingPlansElements.forEach((sellingPlansElement) => {
        if (!sellingPlansElement.shadowRoot) {
          return;
        }

        const sellingPlansShadowRoot = sellingPlansElement.shadowRoot;
        if (!sellingPlansShadowRoot.getElementById(this.sellingPlansStyleElementId)) {
          const styleElement = document.createElement('style');
          styleElement.id = this.sellingPlansStyleElementId;
          styleElement.textContent = `
            .rc-plans-dropdown__select {
              background: #fff !important;
              font-family: 'Brooklyn', sans-serif;
              font-size: 17px;
              font-weight: 400;
              letter-spacing: -1px;
              margin-bottom: 12px;
              border-color: #c2c2c2 !important;
            }

            .rc-plans-dropdown__icon {
              bottom: 6px !important;
            }
          `;

          sellingPlansShadowRoot.appendChild(styleElement);
        }

        this.observeSellingPlansShadowRoot(sellingPlansShadowRoot);
      });
    },

    injectBenefitsStyles(widgetShadowRoot) {
      const benefitsElements = widgetShadowRoot.querySelectorAll('rc-benefits');

      benefitsElements.forEach((benefitsElement) => {
        if (!benefitsElement.shadowRoot) {
          return;
        }

        const benefitsShadowRoot = benefitsElement.shadowRoot;
        if (!benefitsShadowRoot.getElementById(this.benefitsStyleElementId)) {
          const styleElement = document.createElement('style');
          styleElement.id = this.benefitsStyleElementId;
          styleElement.textContent = `
            .rc-benefits__list li {
              font-family: 'Brooklyn', sans-serif;
              font-size: 15px;
              font-weight: 600;
              letter-spacing: -0.6px;
              margin-bottom: 5px !important;
            }

            .rc-benefits__list li::before {
              margin-top: -1px !important;
            }
          `;

          benefitsShadowRoot.appendChild(styleElement);
        }

        this.observeBenefitsShadowRoot(benefitsShadowRoot);
      });
    },

    observeShadowRoot(shadowRoot, index) {
      if (!window.MutationObserver || this.shadowObservers.has(shadowRoot)) {
        return;
      }

      const shadowObserver = new MutationObserver(() => {
        if (!shadowRoot.getElementById(this.styleElementId)) {
          this.injectStyles(shadowRoot, index);
        }
        this.injectSellingPlansStyles(shadowRoot);
        this.injectBenefitsStyles(shadowRoot);
      });

      shadowObserver.observe(shadowRoot, {
        childList: true,
        subtree: true,
      });

      this.shadowObservers.set(shadowRoot, shadowObserver);
    },

    observeSellingPlansShadowRoot(sellingPlansShadowRoot) {
      if (!window.MutationObserver || this.sellingPlansShadowObservers.has(sellingPlansShadowRoot)) {
        return;
      }

      const sellingPlansShadowObserver = new MutationObserver(() => {
        if (!sellingPlansShadowRoot.getElementById(this.sellingPlansStyleElementId)) {
          const styleElement = document.createElement('style');
          styleElement.id = this.sellingPlansStyleElementId;
          styleElement.textContent = `
            .rc-plans-dropdown__select {
              background: #fff !important;
            }
          `;

          sellingPlansShadowRoot.appendChild(styleElement);
        }
      });

      sellingPlansShadowObserver.observe(sellingPlansShadowRoot, {
        childList: true,
        subtree: true,
      });

      this.sellingPlansShadowObservers.set(sellingPlansShadowRoot, sellingPlansShadowObserver);
    },

    observeBenefitsShadowRoot(benefitsShadowRoot) {
      if (!window.MutationObserver || this.benefitsShadowObservers.has(benefitsShadowRoot)) {
        return;
      }

      const benefitsShadowObserver = new MutationObserver(() => {
        if (!benefitsShadowRoot.getElementById(this.benefitsStyleElementId)) {
          const styleElement = document.createElement('style');
          styleElement.id = this.benefitsStyleElementId;
          styleElement.textContent = `
            .rc-benefits__list li {
              font-family: 'Brooklyn', sans-serif;
              font-size: 15px;
              font-weight: 600;
              letter-spacing: -0.6px;
            }
          `;

          benefitsShadowRoot.appendChild(styleElement);
        }
      });

      benefitsShadowObserver.observe(benefitsShadowRoot, {
        childList: true,
        subtree: true,
      });

      this.benefitsShadowObservers.set(benefitsShadowRoot, benefitsShadowObserver);
    },

    startRetryLoop() {
      let attempts = 0;

      const attemptInjection = () => {
        attempts += 1;

        if (this.applyStylesToWidgets()) {
          if (this.retryTimeout) {
            clearTimeout(this.retryTimeout);
            this.retryTimeout = null;
          }
          return;
        }

        if (attempts >= this.maxAttempts) {
          return;
        }

        this.retryTimeout = setTimeout(attemptInjection, this.retryDelay);
      };

      attemptInjection();
    },

    startObserver() {
      if (this.observer || !window.MutationObserver) {
        return;
      }

      this.observer = new MutationObserver(() => {
        this.applyStylesToWidgets();
      });

      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    },

    stopObserver() {
      if (!this.observer) {
        return;
      }

      this.observer.disconnect();
      this.observer = null;

      if (this.retryTimeout) {
        clearTimeout(this.retryTimeout);
        this.retryTimeout = null;
      }
    },
  },

  // Expand Benefit filter on collection pages (desktop only)
  collectionBenefitFilter: {
    /**
     * Expand the Benefit filter details element on collection pages for screens 750px+
     */
    init() {
      // Only run on collection pages
      if (!document.body.classList.contains('page-collection')) {
        return;
      }

      // Only run on screens 750px or wider
      if (window.innerWidth < 750) {
        return;
      }

      // Find the Benefit filter details element
      const benefitFilter = document.getElementById(
        'Details-filter.p.m.futureholidays.benefit-template--15897271271482__product-grid',
      );

      if (benefitFilter && benefitFilter.tagName === 'DETAILS') {
        benefitFilter.setAttribute('open', '');
      }
    },
  },

  // Fix Flickity slider height on mobile Safari for Rebuy widgets
  rebuyFlickityHeight: {
    widgetIds: ['252472', '253923', '254233', '255003'],
    resizeDelay: 250,
    eventDelay: 300,
    resizeHandler: null,
    pendingUpdate: null,
    maxHeights: new Map(),
    heightsSet: new Set(), // Track which widgets have already had their max-height set

    init() {
      // Only run on small screens (mobile)
      if (window.innerWidth > 768) {
        return;
      }

      const eventsToWatch = ['rebuy.init', 'rebuy.ready', 'rebuy.refresh', 'rebuy.productsChange', 'rebuy.show'];

      eventsToWatch.forEach((eventName) => {
        document.addEventListener(eventName, (event) => {
          if (!this.isTargetWidgetEvent(event)) {
            return;
          }
          this.scheduleUpdate();
        });
      });

      // Initial run in case the widget is already rendered
      this.scheduleUpdate();

      // Handle resizes with debounce
      if (!this.resizeHandler) {
        this.resizeHandler = this.debounce(() => {
          // Only run on small screens
          if (window.innerWidth <= 768) {
            this.scheduleUpdate();
          } else {
            // Clear max-heights on larger screens
            this.clearMaxHeights();
          }
        }, this.resizeDelay);
      }
      window.addEventListener('resize', this.resizeHandler);
    },

    debounce(func, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          func(...args);
        }, wait);
      };
    },

    isTargetWidgetEvent(event) {
      const detail = event && event.detail ? event.detail : {};
      const detailId = this.extractWidgetId(detail);

      if (detailId) {
        return this.widgetIds.includes(String(detailId));
      }

      return this.widgetIds.some((id) => Boolean(document.getElementById(`rebuy-widget-${id}`)));
    },

    extractWidgetId(detail) {
      if (!detail) {
        return null;
      }

      if (detail.widget && detail.widget.id) {
        return detail.widget.id;
      }

      if (detail.id) {
        return detail.id;
      }

      if (detail.widgetId) {
        return detail.widgetId;
      }

      return null;
    },

    scheduleUpdate() {
      if (this.pendingUpdate) {
        clearTimeout(this.pendingUpdate);
      }

      this.pendingUpdate = setTimeout(() => {
        window.requestAnimationFrame(() => {
          this.setMaxHeights();
        });
      }, this.eventDelay);
    },

    setMaxHeights() {
      // Only run on small screens
      if (window.innerWidth > 768) {
        return;
      }

      this.widgetIds.forEach((widgetId) => {
        // Skip if we've already set the max-height for this widget
        if (this.heightsSet.has(widgetId)) {
          return;
        }

        const widgetElement = document.getElementById(`rebuy-widget-${widgetId}`);
        if (!widgetElement) {
          return;
        }

        const productGrid = widgetElement.querySelector('.rebuy-product-grid');
        if (!productGrid) {
          return;
        }

        const flickityViewport = productGrid.querySelector('.flickity-viewport');
        if (!flickityViewport) {
          return;
        }

        // Wait longer for all content to load (images, subtitles, etc.)
        // Use multiple checks to ensure content is fully loaded and stable
        let checkCount = 0;
        const maxChecks = 8;
        const checkInterval = 300;
        const heightMeasurements = [];

        const checkHeight = () => {
          checkCount++;

          // Reset max-height temporarily to measure actual height
          const currentMaxHeight = flickityViewport.style.maxHeight;
          flickityViewport.style.maxHeight = '';

          // Force a reflow
          void flickityViewport.offsetHeight;

          // Wait a bit for any animations/transitions to settle
          setTimeout(() => {
            // Measure the height after content is loaded
            const measuredHeight = flickityViewport.getBoundingClientRect().height;

            // Only set max-height if we have a valid measurement and it's larger than a minimum threshold
            // This prevents setting a height that's too small and hides the widget
            if (measuredHeight > 300 && measuredHeight < 1000) {
              // Store measurements to find a stable value
              heightMeasurements.push(measuredHeight);

              // Wait for multiple stable measurements before setting
              if (heightMeasurements.length >= 3) {
                // Sort measurements and take the median (middle value)
                const sorted = [...heightMeasurements].sort((a, b) => a - b);
                const medianHeight = sorted[Math.floor(sorted.length / 2)];

                // Check if measurements are stable (within 50px of each other)
                const min = Math.min(...sorted);
                const max = Math.max(...sorted);
                const isStable = max - min < 50;

                if (isStable) {
                  // Store the max height for this widget (only if we don't have one yet)
                  const storedHeight = this.maxHeights.get(widgetId);

                  // Only set max-height once - use the stable median measurement
                  if (!storedHeight) {
                    this.maxHeights.set(widgetId, medianHeight);
                    flickityViewport.style.maxHeight = `${Math.ceil(medianHeight)}px`;
                    this.heightsSet.add(widgetId);
                  } else {
                    // Already have a stored height, use it and mark as set
                    flickityViewport.style.maxHeight = `${Math.ceil(storedHeight)}px`;
                    this.heightsSet.add(widgetId);
                  }
                } else if (checkCount < maxChecks) {
                  // Measurements not stable yet, check again
                  setTimeout(checkHeight, checkInterval);
                }
              } else if (checkCount < maxChecks) {
                // Need more measurements, check again
                setTimeout(checkHeight, checkInterval);
              }
            } else if (checkCount < maxChecks) {
              // Height out of range or invalid, check again
              setTimeout(checkHeight, checkInterval);
            } else if (currentMaxHeight) {
              // Restore previous max-height if measurement is still invalid after all checks
              flickityViewport.style.maxHeight = currentMaxHeight;
            }
          }, 100);
        };

        // Start checking after longer initial delay to let content settle
        setTimeout(checkHeight, 1200);
      });
    },

    clearMaxHeights() {
      this.widgetIds.forEach((widgetId) => {
        const widgetElement = document.getElementById(`rebuy-widget-${widgetId}`);
        if (!widgetElement) {
          return;
        }

        const productGrid = widgetElement.querySelector('.rebuy-product-grid');
        if (!productGrid) {
          return;
        }

        const flickityViewport = productGrid.querySelector('.flickity-viewport');
        if (flickityViewport) {
          flickityViewport.style.maxHeight = '';
        }
      });

      this.maxHeights.clear();
      this.heightsSet.clear();
    },
  },

  /**
   * Initialize all nebula functionality
   */
  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        nebula.footer.init();
        nebula.rebuy.init();
        nebula.rebuyTitleHeight.init();
        nebula.rebuyReviewCount.init();
        nebula.okendoReviewCount.init();
        nebula.rebuyCleanLinks.init();
        nebula.rebuySubtitle.init();
        nebula.rebuyCardHeights.init();
        nebula.rebuyFlickityHeight.init();
        nebula.smoothScroll.init();
        nebula.collectionCardHeights.init();
        nebula.productSkioTitle.init();
        nebula.rechargeWidgetTypography.init();
        nebula.collectionBenefitFilter.init();
      });
    } else {
      nebula.footer.init();
      nebula.rebuy.init();
      nebula.rebuyTitleHeight.init();
      nebula.rebuyReviewCount.init();
      nebula.okendoReviewCount.init();
      nebula.rebuyCleanLinks.init();
      nebula.rebuySubtitle.init();
      nebula.rebuyCardHeights.init();
      nebula.rebuyFlickityHeight.init();
      nebula.smoothScroll.init();
      nebula.collectionCardHeights.init();
      nebula.productSkioTitle.init();
      nebula.rechargeWidgetTypography.init();
      nebula.collectionBenefitFilter.init();
    }
  },
};

// Auto-initialize when script loads
nebula.init();
